"use client";

import React from "react";
import { I18n } from "aws-amplify/utils";
import { useParams, useNavigate } from "react-router-dom";

import { generateClient } from "aws-amplify/api";

import { Schema } from "../../../amplify/data/resource";
import { Flex, Heading, Link, Text } from "@aws-amplify/ui-react";
import { Helmet } from "react-helmet";
import { FaArrowLeft, FaPencil } from "react-icons/fa6";
import { hasAccess } from "../../acl/groups";
import LoadingDialog from "../../components/LoadingDialog";

const client = generateClient<Schema>();

type Pattern = Schema["Pattern"]["type"];
type Variation = Schema["Variation"]["type"];
type DanceStyle = Schema["DanceStyle"]["type"];

interface VariationViewProps {
  groups: string[];
}

const VariationView: React.FC<VariationViewProps> = ({ groups }) => {
  const navigate = useNavigate();

  const { id } = useParams();

  const [variation, setVariation] = React.useState<Variation>({} as Variation);
  const [danceStyle, setDanceStyle] = React.useState<DanceStyle>(
    {} as DanceStyle
  );
  const [pattern, setPattern] = React.useState<Pattern>({} as Pattern);

  const [loadingMap, setLoadingMap] = React.useState<{
    [key: string]: boolean;
  }>({});

  const markLoading = (key: string, value: boolean) => {
    setLoadingMap((prev) => ({ ...prev, [key]: value }));
  };

  React.useEffect(() => {
    markLoading("variation", true);
    const variationSub = client.models.Variation.observeQuery({
      filter: {
        id: { eq: id },
      },
    }).subscribe({
      next: ({ items }) => {
        setVariation(items[0]);
        markLoading("variation", false);
      },
    });

    return () => {
      variationSub.unsubscribe();
    };
  }, [id]);

  React.useEffect(() => {
    if (variation && variation.patternVariationsId) {
      markLoading("pattern", true);
      client.models.Pattern.observeQuery({
        filter: {
          id: { eq: variation.patternVariationsId },
        },
      }).subscribe({
        next: ({ items }) => {
          setPattern(items[0]);
          markLoading("pattern", false);
        },
      });
    }
  }, [variation]);

  React.useEffect(() => {
    if (pattern && pattern.danceStylePatternsId) {
      markLoading("danceStyle", true);
      client.models.DanceStyle.observeQuery({
        filter: {
          id: { eq: pattern.danceStylePatternsId },
        },
      }).subscribe({
        next: ({ items }) => {
          setDanceStyle(items[0]);
          markLoading("danceStyle", false);
        },
      });
    }
  }, [pattern]);

  return (
    <>
      {Object.values(loadingMap).some((loading) => loading) ? (
        <LoadingDialog loadingMap={loadingMap} />
      ) : (
        <Flex direction="column" gap="1rem">
          <Helmet>
            <title>
              {`${danceStyle?.name} - ${pattern?.name} - ${I18n.get(
                "Variation"
              )} ${"#" + (variation?.order && variation?.order / 10)}`}
            </title>
          </Helmet>
          <Flex marginTop={"1rem"}>
            <Heading level={1}>
              {`${danceStyle?.name} - ${pattern?.name} - ${I18n.get(
                "Variation"
              )} ${"#" + (variation?.order && variation?.order / 10)}`}
            </Heading>
          </Flex>
          <Flex>
            <Link onClick={() => navigate(-1)}>
              <FaArrowLeft aria-label={I18n.get("Back")} />
            </Link>
            {hasAccess(groups, "variations", "update") && (
              <Link href={`/variations/${variation?.id}/update`}>
                <FaPencil aria-label={I18n.get("Edit")} />
              </Link>
            )}
          </Flex>
          <Flex direction="column">
            <Flex>
              <Heading>{I18n.get("Dance Style")}</Heading>
              <Text>
                <Link
                  href={`/dance-styles/${pattern?.danceStylePatternsId}/view`}
                >
                  {danceStyle
                    ? danceStyle?.name
                    : pattern?.danceStylePatternsId}
                </Link>
              </Text>
            </Flex>
            <Flex>
              <Heading>{I18n.get("Pattern")}</Heading>
              <Text>
                <Link href={`/patterns/${variation?.patternVariationsId}/view`}>
                  {pattern ? pattern?.name : variation?.patternVariationsId}
                </Link>
              </Text>
            </Flex>
            <Flex>
              <Heading>{I18n.get("Rhythm")}</Heading>
              <Text>{variation?.rhytm}</Text>
              {variation?.rhytmMan && (
                <Text>
                  {I18n.get("Man")}: {variation?.rhytmMan}
                </Text>
              )}
            </Flex>
            <Flex>
              <Heading>{I18n.get("Hold")}</Heading>
              <Text>{variation?.hold}</Text>
            </Flex>
            <Flex>
              <Heading>{I18n.get("Status")}</Heading>
              <Text>
                {variation?.status ? I18n.get("Active") : I18n.get("Disabled")}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      )}
    </>
  );
};

export default VariationView;
